import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useRide } from '../contexts/RideContext';
import { format, formatDistanceToNow } from 'date-fns';
import { MapPin, Package, Utensils, Briefcase, ArrowRight, Loader2, X, Star, Clock, Shield, Truck, Navigation, Utensils as UtensilsIcon, Briefcase as BriefcaseIcon, Filter, ChevronDown } from 'lucide-react';

const statusLabels: Record<string, string> = {
  requested: 'Demandée',
  searching: 'Recherche livreur',
  assigned: 'Assignée',
  accepted: 'Acceptée',
  en_route_pickup: 'En route vers le pickup',
  picked_up: 'Récupéré',
  en_route_delivery: 'En livraison',
  delivered: 'Livré',
  completed: 'Terminée',
  cancelled: 'Annulée',
  expired: 'Expirée',
};

const typeLabels: Record<string, string> = {
  delivery: 'Livraison',
  errand: 'Course',
  meal: 'Repas',
  package: 'Colis',
};

const typeIcons: Record<string, any> = {
  delivery: Package,
  errand: Navigation,
  meal: UtensilsIcon,
  package: BriefcaseIcon,
};

const statusColors: Record<string, string> = {
  completed: 'bg-green-100 text-green-600',
  delivered: 'bg-green-100 text-green-600',
  cancelled: 'bg-red-100 text-red-600',
  expired: 'bg-gray-100 text-gray-600',
  accepted: 'bg-blue-100 text-blue-600',
  picked_up: 'bg-indigo-100 text-indigo-600',
  en_route_delivery: 'bg-purple-100 text-purple-600',
  en_route_pickup: 'bg-purple-100 text-purple-600',
  searching: 'bg-amber-100 text-amber-600',
};

function RideTypeIcon({ type }: { type: string }) {
  const Icon = typeIcons[type] || Package;
  return <Icon size={20} />;
}

const dropdownArrowStyle: React.CSSProperties = {
  backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'24\' height=\'24\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%2364748B\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'%3E%3C/polyline%3E%3C/svg%3E")',
  backgroundPosition: 'right 12px center',
  backgroundRepeat: 'no-repeat',
};

export default function RideHistory() {
  const { rides, searchRides } = useRide();
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'completed' | 'cancelled'>('all');
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    const loadRides = async () => {
      setLoading(true);
      setError('');
      try {
        const result = await searchRides({ page, limit: 20 });
        setTotal(result.total);
      } catch (err) {
        setError('Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };
    loadRides();
  }, [page, searchRides]);

  const filteredRides = rides.filter(ride => {
    if (filter === 'all') return true;
    if (filter === 'active') return !['completed', 'delivered', 'cancelled', 'expired'].includes(ride.status);
    if (filter === 'completed') return ['completed', 'delivered'].includes(ride.status);
    if (filter === 'cancelled') return ['cancelled', 'expired'].includes(ride.status);
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="loading-spinner-lg mx-auto mb-4 text-primary" />
          <p className="text-text-secondary">Chargement de l'historique...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container pt-4 pb-28">
        <div className="flex items-center justify-between mb-6 animate-fade-in">
          <h1 className="text-xl font-semibold text-text">Historique des courses</h1>
        </div>

        {/* Filter Bar */}
        <div className="card mb-5 animate-slide-up">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Filter size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
              <select
                value={filter}
                onChange={e => { setFilter(e.target.value as any); setPage(1); }}
                className="input-field pl-10 pr-8 appearance-none bg-white"
                style={dropdownArrowStyle}
              >
                <option value="all">Toutes</option>
                <option value="active">En cours</option>
                <option value="completed">Terminées</option>
                <option value="cancelled">Annulées</option>
              </select>
            </div>
            <span className="badge badge-gray text-sm">
              {total} course{total > 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2 animate-slide-up">
            <X size={16} />
            {error}
          </div>
        )}

        {filteredRides.length === 0 ? (
          <div className="card text-center py-16 animate-fade-in">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Package size={32} className="text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-text mb-2">Aucune course</h3>
            <p className="text-text-secondary mb-6">
              {filter === 'all' ? 'Commencez par créer votre première livraison' : `Aucune course ${filter === 'active' ? 'en cours' : filter}`}
            </p>
            <Link to="/new" className="btn btn-primary inline-block">
              Nouvelle livraison
            </Link>
          </div>
        ) : (
          <>
            <div className="space-y-3 animate-fade-in">
              {filteredRides.map((ride, index) => {
                const TypeIcon = typeIcons[ride.type] || Package;
                return (
                  <Link key={ride.id} to={`/ride/${ride.id}`} className="ride-card group animate-slide-up" style={{textDecoration: 'none', animationDelay: `${index * 50}ms`}}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                          <TypeIcon size={22} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-text truncate">{typeLabels[ride.type] || ride.type}</p>
                            <span className={`badge badge-sm ${statusColors[ride.status] || 'bg-primary/10 text-primary'}`}>
                              {statusLabels[ride.status] || ride.status}
                            </span>
                          </div>
                          <p className="text-sm text-text-secondary truncate">
                            {ride.pickupAddress} → {ride.dropoffAddress}
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-bold text-text">{ride.priceFcfa.toLocaleString()} FCFA</p>
                        <p className="text-xs text-text-secondary">{formatDistanceToNow(new Date(ride.createdAt), { addSuffix: true })}</p>
                        <p className="text-xs text-text-secondary">{format(new Date(ride.createdAt), 'dd/MM/yyyy HH:mm')}</p>
                      </div>
                    </div>
                    <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl" />
                  </Link>
                );
              })}
            </div>

            {total > page * 20 && (
              <div className="text-center mt-8 animate-fade-in">
                <button
                  onClick={() => setPage(p => p + 1)}
                  className="btn btn-outline btn-lg"
                >
                  Charger plus ({filteredRides.length}/{total})
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}